// Feedback.jsx — v160.4.93
//
// My feedback page with per-app grouping + multi-select for auto-fix.
//
// Each feedback item gets a checkbox. The tester picks any combination
// from a single app/build (server-side validation enforces this) and
// taps "Send for auto-fix" — that POSTs to
// /v1/dist/me/feedback/submit-fix-request, then redirects to
// /fix-progress/<requestId> where they watch the live timeline.

import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { me, submitFixRequest } from '../lib/api.js';

const STATE_BADGE = {
  open: 'badge-info',
  investigating: 'badge-warning',
  resolved: 'badge-success',
  // v160.4.99x/y: verification-loop states. Pending verification is
  // distinct from resolved — fix shipped, but we don't know yet
  // whether it actually worked. Verified is the official success.
  // Fix-failed re-opens the row for human attention.
  resolved_pending_verification: 'badge-warning',
  resolved_verified: 'badge-success',
  resolved_fix_failed: 'badge-error',
  wontfix: 'badge',
  dupe: 'badge'
};

const FIX_STATE_BADGE = {
  requested:    { label: '⏳ queued',     bg: 'rgba(108,92,231,0.18)', color: '#a29bfe' },
  in_progress:  { label: '🔧 fixing',     bg: 'rgba(245,158,11,0.18)',  color: '#f59e0b' },
  fix_attempted:{ label: '🛠 attempted',  bg: 'rgba(108,92,231,0.18)', color: '#a29bfe' },
  fix_confirmed:{ label: '✅ fixed',      bg: 'rgba(16,185,129,0.18)',  color: '#10b981' },
  fix_failed:   { label: '❌ failed',     bg: 'rgba(239,68,68,0.18)',   color: '#ef4444' },
  regressed:    { label: '⚠️ regressed', bg: 'rgba(245,158,11,0.18)',  color: '#f59e0b' }
};

export default function Feedback() {
  const navigate = useNavigate();
  const [items, setItems] = useState(null);
  const [error, setError] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [submitting, setSubmitting] = useState(false);
  // v160.4.99f: per-item delete state. Tracks the row currently being
  // deleted so we can disable just that one's button + show "Deleting…"
  // without freezing the whole list (multi-row delete is rare but possible
  // because the backend serializes via SELECT FOR UPDATE).
  const [deletingId, setDeletingId] = useState(null);

  useEffect(() => {
    me.feedback().then(r => setItems(r.feedback)).catch(e => setError(e.message));
  }, []);

  /**
   * v160.4.99f: hard-delete a single feedback row.
   *
   * Confirms first because this is irreversible — there's no undo on the
   * backend side. UX-wise this is a tester self-cleanup tool (mistyped a
   * report, tagged the wrong element, hit submit twice). Optimistic update:
   * remove from local state immediately on success so the list doesn't
   * flash through a refetch. On 409 (fix already in progress), the backend
   * sends a `detail` field that explains what to do; surface it verbatim.
   */
  async function deleteItem(item) {
    if (deletingId) return;            // ignore double-tap
    const ok = window.confirm(
      `Delete this feedback?\n\n"${(item.message || '').slice(0, 120)}${(item.message || '').length > 120 ? '…' : ''}"\n\nThis can't be undone.`
    );
    if (!ok) return;

    setDeletingId(item.id);
    try {
      await me.deleteFeedback(item.id);
      // Drop from items + clear from selection if it was checked.
      setItems(prev => prev ? prev.filter(it => it.id !== item.id) : prev);
      setSelected(prev => {
        if (!prev.has(item.id)) return prev;
        const next = new Set(prev); next.delete(item.id); return next;
      });
    } catch (e) {
      // v160.4.99f: prefer the structured fields (e.error + e.detail set by
      // the api request helper) so 409 In-Progress comes through with the
      // useful "Wait for the fix attempt to finish" detail line. Fall
      // back to e.message which is `<status>: <error>`.
      const headline = e.error || e.message;
      const msg = e.detail ? `${headline}\n\n${e.detail}` : headline;
      alert(`Couldn't delete: ${msg}`);
    } finally {
      setDeletingId(null);
    }
  }

  const groups = useMemo(() => {
    if (!items) return null;
    const m = new Map();
    for (const f of items) {
      const key = `${f.app_slug}::${f.build_id || 'unknown'}`;
      if (!m.has(key)) m.set(key, { appSlug: f.app_slug, buildId: f.build_id, items: [] });
      m.get(key).items.push(f);
    }
    return Array.from(m.values()).sort((a, b) => {
      const aMax = Math.max(...a.items.map(x => new Date(x.created_at).getTime()));
      const bMax = Math.max(...b.items.map(x => new Date(x.created_at).getTime()));
      return bMax - aMax;
    });
  }, [items]);

  function toggle(id) {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  async function submitGroup(group) {
    const ids = group.items.filter(it => selected.has(it.id)).map(it => it.id);
    if (ids.length === 0) {
      alert('Select at least one feedback item from this group first.');
      return;
    }
    if (!group.buildId || group.buildId === 'unknown') {
      alert('These feedback items aren\'t linked to a specific build — auto-fix needs a build to patch against.');
      return;
    }
    setSubmitting(true);
    try {
      const r = await submitFixRequest({
        feedbackIds: ids, appSlug: group.appSlug, buildId: group.buildId
      });
      setSelected(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
      navigate(`/fix-progress/${r.requestId}`);
    } catch (e) {
      alert(`Submit failed: ${e.message}`);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div>
      <h2 style={{ marginBottom: 8 }}>My feedback</h2>
      <p style={{color: 'var(--text-muted)', marginTop: 0, marginBottom: 16, fontSize: 14}}>
        Pick the items you want fixed and tap <b>Send for auto-fix</b>.
        Claude will patch the code and ship you a new build automatically.
      </p>

      <div className="card" style={{ background: 'rgba(108,92,231,0.08)', fontSize: 13, marginBottom: 12 }}>
        💡 To report a new bug: open the app you're testing and <strong>shake your device</strong>.
        A picker appears — tap any UI element to attach feedback.
      </div>

      {/* v161.22.29: alternative submission paths */}
      <div className="card" style={{ background: 'rgba(108,92,231,0.08)', fontSize: 13, marginBottom: 20 }}>
        📄 Prefer to write a longer report? You can also
        {' '}<Link to="/reports/new" style={{ color: 'var(--accent)', fontWeight: 600 }}>start a structured bug report</Link>
        {' '}or{' '}
        <Link to="/reports/upload-pdf" style={{ color: 'var(--accent)', fontWeight: 600 }}>upload a PDF</Link>
        {' '}drafted in Word or Google Docs.
      </div>

      {error && <div className="error-banner">{error}</div>}
      {items === null && <div className="spinner" />}
      {items && items.length === 0 && <div className="empty">No feedback submitted yet.</div>}

      {groups && groups.map(g => {
        const selCount = g.items.filter(it => selected.has(it.id)).length;
        const anyInProgress = g.items.some(it => it.fix_request_state === 'requested' || it.fix_request_state === 'in_progress');
        // v160.4.93c: orphaned feedback (no build_id linked). Happens when
        // the SDK couldn't resolve its build_id at submit time (network blip,
        // build not yet uploaded). These items can't be sent for auto-fix
        // because the auto-fix prompt needs a source build to patch against.
        const isOrphaned = !g.buildId || g.buildId === 'unknown';
        return (
          <div key={`${g.appSlug}-${g.buildId}`} style={{
            background: 'var(--bg-elev)', borderRadius: 14,
            padding: 16, marginBottom: 16, border: '1px solid var(--border)'
          }}>
            <div style={{display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 12}}>
              <div>
                <span className="badge badge-accent">{g.appSlug}</span>
                {g.buildId && !isOrphaned && (
                  <span className="badge" style={{marginLeft: 6, fontFamily: 'SF Mono, Menlo, monospace', fontSize: 10}}>
                    build {g.buildId.slice(0, 8)}
                  </span>
                )}
                {isOrphaned && (
                  <span className="badge" style={{marginLeft: 6, color: 'var(--warning, #f59e0b)', fontSize: 10}}>
                    ⚠️ no build linked
                  </span>
                )}
              </div>
              <span style={{color: 'var(--text-muted)', fontSize: 12}}>
                {g.items.length} item{g.items.length === 1 ? '' : 's'}
              </span>
            </div>

            {isOrphaned && (
              <div style={{
                fontSize: 12, color: 'var(--text-muted)',
                background: 'rgba(245,158,11,0.08)', padding: '8px 10px',
                borderRadius: 8, marginBottom: 10
              }}>
                These reports aren't linked to a specific build, so auto-fix
                can't process them. This usually means the app couldn't reach
                the backend when the report was submitted. Newer reports
                from the same app should link correctly.
              </div>
            )}

            {g.items.map(f => {
              const fb = FIX_STATE_BADGE[f.fix_request_state];
              const checked = selected.has(f.id);
              const pending = f.fix_request_state === 'requested' || f.fix_request_state === 'in_progress';
              return (
                <div key={f.id} style={{
                  display: 'flex', alignItems: 'flex-start', gap: 12,
                  padding: '10px 0', borderBottom: '1px solid var(--border-subtle, #2a2a35)'
                }}>
                  {!isOrphaned && (
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={pending}
                      onChange={() => toggle(f.id)}
                      style={{marginTop: 4, transform: 'scale(1.2)'}}
                    />
                  )}
                  <div style={{flex: 1}}>
                    <div style={{fontSize: 14, marginBottom: 4}}>{f.message}</div>
                    <div style={{display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap'}}>
                      <span style={{fontSize: 11, color: 'var(--text-muted)'}}>
                        {new Date(f.created_at).toLocaleString()}
                      </span>
                      <span className={`badge ${STATE_BADGE[f.state] || ''}`}>{f.state}</span>
                      {fb && (
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 4,
                          background: fb.bg, color: fb.color, fontWeight: 600
                        }}>
                          {fb.label}
                        </span>
                      )}
                      {f.severity && f.severity !== 'normal' && (
                        <span className="badge" style={{fontSize: 10}}>{f.severity}</span>
                      )}
                      {f.tagged_element && (
                        <span style={{
                          fontSize: 10, padding: '2px 8px', borderRadius: 4,
                          background: 'rgba(108,92,231,0.18)', color: '#a29bfe', fontWeight: 600
                        }}>
                          🎯 {f.tagged_element.accessibilityIdentifier
                              || f.tagged_element.accessibilityLabel
                              || f.tagged_element.className}
                        </span>
                      )}
                    </div>
                  </div>
                  {/*
                    v160.4.99f: per-row delete. Disabled when:
                      • a fix is in flight (`pending`) — backend returns 409
                        anyway, but disabling here gives clearer UX with
                        the explanatory tooltip
                      • another row in the list is mid-delete (deletingId)
                    Title attribute serves as a hover hint on desktop and is
                    fine to ignore on mobile. The icon-only button keeps the
                    row compact; mobile users tap-and-confirm the same way
                    as the auto-fix button.
                  */}
                  <button
                    onClick={() => deleteItem(f)}
                    disabled={pending || deletingId !== null}
                    title={pending
                      ? 'Cannot delete: a fix is already in progress for this report'
                      : 'Delete this feedback (cannot be undone)'}
                    aria-label="Delete feedback"
                    style={{
                      flexShrink: 0,
                      padding: '4px 10px',
                      fontSize: 13,
                      borderRadius: 6,
                      background: 'transparent',
                      color: pending ? 'var(--text-muted)' : '#ef4444',
                      border: '1px solid ' + (pending ? 'var(--border)' : 'rgba(239,68,68,0.4)'),
                      cursor: (pending || deletingId !== null) ? 'not-allowed' : 'pointer',
                      opacity: (pending || deletingId === f.id) ? 0.55 : 1,
                      alignSelf: 'flex-start'
                    }}>
                    {deletingId === f.id ? '…' : '🗑'}
                  </button>
                </div>
              );
            })}

            {!isOrphaned && selCount > 0 && (
              <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                marginTop: 14, paddingTop: 12, borderTop: '1px solid var(--border)'
              }}>
                <span style={{fontSize: 13, color: 'var(--text-muted)'}}>
                  {selCount} selected
                </span>
                <button
                  onClick={() => submitGroup(g)}
                  disabled={submitting || anyInProgress}
                  style={{
                    background: 'var(--accent)', color: 'white',
                    padding: '10px 16px', borderRadius: 10,
                    fontWeight: 600, fontSize: 14,
                    opacity: submitting || anyInProgress ? 0.5 : 1,
                    cursor: submitting || anyInProgress ? 'default' : 'pointer'
                  }}>
                  {submitting ? 'Sending…' : anyInProgress ? 'Fix already in progress' : '🤖 Send for auto-fix'}
                </button>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
